import {Shift} from './types';
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import HttpClient from '../../api/api';

export interface ShiftsState {
    items: Shift[];
}

const initialState: ShiftsState = {
    items: []
}

const upsertShift = (state: ShiftsState, shift: Shift) => {
    const index = state.items.findIndex((item) => item.id === shift.id);

    if (index !== -1) {
        state.items[index] = shift;
        return;
    }

    state.items.push(shift);
}

export const getShift = createAsyncThunk(
    'shifts/get',
    async (id: number) => {
        const response = await HttpClient.shiftsApi.get<Shift>(id);
        return response.data;
    }
)

export const getShifts = createAsyncThunk(
    'shifts/get-many',
    async (
        args: { dateFrom: string, dateTo: string, employees: Array<number>, projects: Array<number>, types: Array<number> },
        { signal }
    ) => {
        const response = await HttpClient.shiftsApi.getMany<Shift[]>(args.dateFrom, args.dateTo, args.employees, args.projects, args.types, signal);
        return response.data;
    }
)

export const createShift = createAsyncThunk(
    'shifts/create',
    async (shift: Shift) => {
        const response = await HttpClient.shiftsApi.create<Shift>(shift);
        return response.data;
    }
)

export const updateShift = createAsyncThunk(
    'shifts/update',
    async (shift: Shift) => {
        const response = await HttpClient.shiftsApi.update<Shift>(shift.id, shift);
        return response.data
    }
)

/*todo */
export const shiftApprove = createAsyncThunk(
    'shifts/approved',
    async (args: { id: number, approve: boolean }, { rejectWithValue }) => {
        try {
            const response = await HttpClient.shiftsApi.updateApproved(args.id, args.approve);
            if (response.status !== 200) {
                return rejectWithValue(response.statusText);
            }
            return args;
        } catch (error) {
            return rejectWithValue('Unable to approve shift')
        }
    }
)
/*todo */
export const shiftBillable = createAsyncThunk(
    'shifts/billable',
    async (args: { id: number, billed: boolean }, { rejectWithValue }) => {
        try {
            const response = await HttpClient.shiftsApi.updateBillable(args.id, args.billed);
            if (response.status !== 200) {
                return rejectWithValue(response.statusText);
            }
            return args;
        } catch (error) {
            return rejectWithValue('Unable to bill shift')
        }
    }
)

export const deleteShift = createAsyncThunk(
    'shifts/delete',
    async (shiftId: number) => {
        const response = await HttpClient.shiftsApi.delete(shiftId);
        return response.data;
    }
)

export const shiftBatchApprove = createAsyncThunk(
    'shifts/batch-approve',
    async (args: { ids: Array<number>, approve: boolean }, { rejectWithValue }) => {
        if (args.ids.length < 1) {
            return { ids: [], approve: false };
        }
        try {
            const response = await HttpClient.shiftsApi.batchApprove(args.ids, args.approve);
            if (response.status !== 200) {
                return rejectWithValue(response.statusText);
            }
            return args;
        } catch (error) {
            return rejectWithValue('Unable to batch approve shifts')
        }
    }
)

export const shiftBatchBillable = createAsyncThunk(
    'shifts/batch-billable',
    async (args: { ids: Array<number>, billed: boolean }, { rejectWithValue }) => {
        if (args.ids.length < 1) {
            return { ids: [], billed: false };
        }
        try {
            const response = await HttpClient.shiftsApi.batchBillable(args.ids, args.billed);
            if (response.status !== 200) {
                return rejectWithValue(response.statusText);
            }
            return args;
        } catch (error) {
            return rejectWithValue('Unable to batch bill shifts')
        }
    }
)

const shiftsSlice = createSlice({
    name: 'shifts',
    initialState,
    reducers: {
        clearShifts: (state) => {
            state.items = initialState.items;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getShifts.fulfilled, (state, action) => {
            state.items = action.payload;
        });
        builder.addCase(updateShift.fulfilled, (state, action) => {
            upsertShift(state, action.payload);
        });
        builder.addCase(getShift.fulfilled, (state, action) => {
            upsertShift(state, action.payload);
        });
        builder.addCase(createShift.fulfilled, (state, action) => {
            upsertShift(state, action.payload);
        });
        builder.addCase(deleteShift.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.id !== action.meta.arg);
        });
        builder.addCase(shiftBatchApprove.fulfilled, (state, action) => {
            action.payload.ids.forEach((x) => {
                const index = state.items.findIndex((y) => y.id === x);
                if (index != -1) {
                    state.items[index].approved = action.payload.approve;
                }
            });
        });
        builder.addCase(shiftBatchBillable.fulfilled, (state, action) => {
            action.payload.ids.forEach((x) => {
                const index = state.items.findIndex((y) => y.id === x);
                if (index != -1) {
                    state.items[index].billable = action.payload.billed;
                }
            });
        });
        builder.addCase(shiftApprove.fulfilled, (state, action) => {
            const index = state.items.findIndex((y) => y.id === action.payload.id);
            if (index != -1) {
                state.items[index].approved = action.payload.approve;
            }
        });
        builder.addCase(shiftBillable.fulfilled, (state, action) => {
            const index = state.items.findIndex((y) => y.id === action.payload.id);
            if (index != -1) {
                state.items[index].billable = action.payload.billed;
            }
        });
    }
})

export const { clearShifts } = shiftsSlice.actions;

export default shiftsSlice.reducer;
